'use client';

import { useMemo } from 'react';
import { DownloadFile, s3DateStringToDate } from '@drift/common';
import { downloadFileFromS3 } from 'src/utils/dom';
import Button from 'src/components/primitives/Button/Button';
import { PortfolioHistoryExportTypeFilter } from 'src/components/PortfolioPage/PortfolioHistorySubPage/PortfolioHistoryExportsTable/types';
import { createColumnHelper } from '@tanstack/react-table';
import TableV2 from 'src/components/Tables/TableV2';
import useExportsTable from 'src/components/PortfolioPage/PortfolioHistorySubPage/PortfolioHistoryExportsTable/ExportsTable/hooks/useExportsTable';
import {
	getExportsTableDataTitleFromType,
	getExportsTableDownloadButtonText,
	getExportsTableFileIsEmpty,
	getExportsTableLiquidatee,
} from 'src/components/PortfolioPage/PortfolioHistorySubPage/PortfolioHistoryExportsTable/ExportsTable/utils';
import ExportsTableMobileCard from 'src/components/PortfolioPage/PortfolioHistorySubPage/PortfolioHistoryExportsTable/ExportsTable/ExportsTableMobileCard';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

interface Props {
	exportTypeFilter: PortfolioHistoryExportTypeFilter;
}

const columnHelper = createColumnHelper<DownloadFile>();

export default function ExportsTable({ exportTypeFilter }: Props) {
	const { data, pagination, accounts, deletedAccounts, isLoading } =
		useExportsTable({ exportTypeFilter });

	const columns = useMemo(() => {
		return [
			columnHelper.accessor('requestParams.fileType', {
				header: 'Data',
				cell: ({ getValue }) => getExportsTableDataTitleFromType(getValue()),
				meta: {
					width: { ratio: 5, minPx: 180 },
					bodyCellClassName: 'capitalize',
				},
			}),
			columnHelper.accessor('requestParams.marketSymbol', {
				header: 'Liquidatee',
				cell: ({ getValue, row }) =>
					getExportsTableLiquidatee({
						marketSymbol: getValue(),
						recordUserKey: row.original.requestParams.user,
						deletedAccounts,
						accounts,
					}),
				meta: {
					width: { ratio: 5, minPx: 180 },
				},
			}),
			columnHelper.accessor('requestParams.requestedDate', {
				header: 'Generating Date',
				cell: ({ getValue }) => (
					<TableV2.DateCell
						date={s3DateStringToDate(getValue())}
						displayAsTimestamp
						hideTime
					/>
				),
				meta: { width: { ratio: 5, minPx: 180 } },
			}),
			columnHelper.accessor('requestParams.fromDate', {
				header: 'Start Date',
				cell: ({ getValue }) => (
					<TableV2.DateCell
						date={s3DateStringToDate(getValue())}
						displayAsTimestamp
						hideTime
					/>
				),
				meta: { width: { ratio: 5, minPx: 180 } },
			}),
			columnHelper.accessor('requestParams.toDate', {
				header: 'End Date',
				cell: ({ getValue }) => (
					<TableV2.DateCell
						date={s3DateStringToDate(getValue())}
						displayAsTimestamp
						hideTime
					/>
				),
				meta: { width: { ratio: 5, minPx: 180 } },
			}),
			columnHelper.display({
				header: 'Action',
				cell: ({ row }) => {
					const downloadUrl = row.original.downloadUrl;
					const fileIsEmpty = getExportsTableFileIsEmpty(downloadUrl);
					const buttonText = getExportsTableDownloadButtonText(downloadUrl);

					return (
						<Button
							size="sm"
							variant="secondary"
							disabled={!downloadUrl || fileIsEmpty}
							onClick={() => {
								if (!downloadUrl) {
									return;
								}

								downloadFileFromS3(downloadUrl);
							}}
						>
							{buttonText}
						</Button>
					);
				},
				meta: {
					width: { ratio: 3, minPx: 144 },
					headerCellClassName: 'text-right',
					bodyCellClassName: 'flex items-center justify-end',
				},
			}),
		];
	}, [deletedAccounts, accounts]);

	return (
		<HistoryTable
			dataTableProps={{
				columns,
				data,
				emptyMessage: 'No exports found',
				pagination,
			}}
			mobileCard={({ record: { key, requestParams: r } }) => (
				<ExportsTableMobileCard
					key={`${key}-${r.requestedDate}-${r.user}`}
					fileType={r.fileType}
					marketSymbol={r.marketSymbol}
					recordUserKey={r.user}
					deletedAccounts={deletedAccounts}
					accounts={accounts}
					requestedDate={r.requestedDate}
					fromDate={r.fromDate}
					toDate={r.toDate}
				/>
			)}
			isLoading={isLoading}
		/>
	);
}
